/**
 * De editor-state van de sitebouwer, als één pure reducer.
 *
 * Vier soorten state leven hier bewust náást elkaar in plaats van door
 * elkaar:
 *
 * - **inhoud**: `pages` (lokaal bewerkbaar) tegenover `revision` (het token
 *   van de laatst bevestigde serverversie);
 * - **selectie**: welke pagina en welk blok de gebruiker onder handen heeft;
 * - **opslag**: `saveStatus`, `changeSeq`/`savedSeq`, conflict;
 * - **geschiedenis**: `past`/`future` voor ongedaan maken.
 *
 * Alles wat de inhoud verandert loopt via `commit()`, zodat er geen enkele
 * bewerking bestaat die wel de pagina's wijzigt maar niet in de
 * geschiedenis of de opslagstatus terechtkomt.
 */
import { createBlock } from '../blocks/registry';
import { createBlockId, createPageId } from '../ids';
import { slugFromTitle, uniqueSlug } from '../slug';
import { COLUMN_COUNTS } from '../types';
import { layoutFromColumn, setPlacement } from '../grid';
import { materializeSectionHeight } from '../render/sectionGeometry';
import { configuredFooterMinimumHeight } from '../freeformFooter';
import type { BlockType, BuilderDraft, ColumnLayout, ContentBlock, GridPlacement, SaveStatus, SiteChrome, SitePage, SiteSection, ViewportKey } from '../types';

const MAX_HISTORY = 100;

export interface HistorySnapshot {
  pages: SitePage[];
  site: SiteChrome | null;
  selectedPageId: string | null;
  selectedSectionId: string | null;
  selectedBlockId: string | null;
}

export interface EditorState {
  status: 'loading' | 'ready' | 'error';
  loadError: string | null;

  draftId: string | null;
  websiteId: string | null;
  /** Concurrency-token van de laatst bevestigde serverversie. */
  revision: number;
  pages: SitePage[];
  site: SiteChrome | null;

  selectedPageId: string | null;
  selectedSectionId: string | null;
  selectedBlockId: string | null;

  /** Loopt op bij elke inhoudelijke wijziging. */
  changeSeq: number;
  /** De `changeSeq` waarvan de server bevestigd heeft dat hij is opgeslagen. */
  savedSeq: number;
  /** De `changeSeq` die op dit moment onderweg is naar de server. */
  inFlightSeq: number | null;

  saveStatus: SaveStatus;
  saveError: string | null;
  lastSavedAt: number | null;

  /** Gevuld zodra de server een 409 gaf: iemand anders was eerder. */
  conflict: { serverDraft: BuilderDraft } | null;

  past: HistorySnapshot[];
  future: HistorySnapshot[];
  /**
   * Groepeert opeenvolgende typwijzigingen in hetzelfde blok tot één
   * ongedaan-maak-stap. Zonder dit zou elke aanslag een aparte stap zijn en
   * is ongedaan maken onbruikbaar.
   */
  coalesceKey: string | null;
}

export const initialEditorState: EditorState = {
  status: 'loading',
  loadError: null,
  draftId: null,
  websiteId: null,
  revision: 0,
  pages: [],
  site: null,
  selectedPageId: null,
  selectedSectionId: null,
  selectedBlockId: null,
  changeSeq: 0,
  savedSeq: 0,
  inFlightSeq: null,
  saveStatus: 'idle',
  saveError: null,
  lastSavedAt: null,
  conflict: null,
  past: [],
  future: [],
  coalesceKey: null,
};

export type EditorAction =
  | { type: 'loadStarted' }
  | { type: 'loaded'; draft: BuilderDraft }
  | { type: 'loadFailed'; message: string }
  | { type: 'selectPage'; pageId: string }
  | { type: 'selectSection'; sectionId: string | null }
  | { type: 'selectBlock'; blockId: string | null }
  | { type: 'updateSite'; patch: Partial<SiteChrome> }
  | { type: 'updateTheme'; patch: Partial<SiteChrome['theme']> }
  | { type: 'updateHeader'; patch: Partial<SiteChrome['header']> }
  | { type: 'updateFooter'; patch: Partial<SiteChrome['footer']> }
  | { type: 'updateSettings'; patch: Partial<SiteChrome['settings']> }
  | { type: 'updateSection'; sectionId: string; patch: Partial<SiteSection> }
  | { type: 'moveSection'; sectionId: string; direction: 'up' | 'down' }
  | { type: 'reorderSections'; from: number; to: number }
  | { type: 'changeSectionLayout'; sectionId: string; layout: ColumnLayout }
  | { type: 'moveBlockToColumn'; blockId: string; sectionId: string; columnId: string; index?: number }
  | { type: 'reorderBlockInColumn'; sectionId: string; columnId: string; from: number; to: number }
  | { type: 'addPage'; title: string; slug: string; description?: string | null }
  | { type: 'updatePage'; pageId: string; patch: Partial<Pick<SitePage, 'title' | 'slug' | 'description'>> }
  | { type: 'deletePage'; pageId: string }
  | { type: 'duplicatePage'; pageId: string }
  | { type: 'makeHomepage'; pageId: string }
  | { type: 'addBlock'; blockType: BlockType; index?: number; sectionId?: string; columnId?: string }
  | { type: 'addSection'; blocks: ContentBlock[]; index?: number; columnCount?: number; columnLayout?: ColumnLayout; style?: SiteSection['style']; presetId?: string }
  | { type: 'insertSection'; section: Omit<SiteSection, 'sectionId' | 'layout' | 'spacing'>; index?: number }
  | { type: 'deleteSection'; sectionId: string }
  | { type: 'duplicateSection'; sectionId: string }
  | { type: 'setBlockText'; blockId: string; text: string }
  | { type: 'updateBlock'; blockId: string; patch: Partial<ContentBlock> }
  | { type: 'updateBlockPlacement'; blockId: string; viewport: ViewportKey; patch: Partial<GridPlacement> }
  | { type: 'deleteBlock'; blockId: string }
  | { type: 'duplicateBlock'; blockId: string }
  | { type: 'moveBlock'; from: number; to: number }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'saveStarted'; seq: number }
  | { type: 'saveSucceeded'; seq: number; revision: number }
  | { type: 'saveFailed'; message: string }
  | { type: 'saveConflict'; serverDraft: BuilderDraft }
  | { type: 'resolveConflictTakeServer' }
  | { type: 'resolveConflictKeepMine' }
  | { type: 'dismissError' };

export function isDirty(state: EditorState): boolean {
  return state.changeSeq !== state.savedSeq;
}

export function currentPage(state: EditorState): SitePage | null {
  return state.pages.find((page) => page.pageId === state.selectedPageId) ?? null;
}

export function currentBlock(state: EditorState): ContentBlock | null {
  const page = currentPage(state);
  if (!page || !state.selectedBlockId) return null;
  return page.sections?.flatMap((section) => section.columns.flatMap((column) => column.blocks)).find((block) => block.blockId === state.selectedBlockId) ?? null;
}

export function currentSection(state: EditorState): SiteSection | null {
  const page = currentPage(state);
  return page?.sections?.find((section) => section.sectionId === state.selectedSectionId) ?? null;
}

export function homepageOf(pages: SitePage[]): SitePage | null {
  return pages.find((page) => page.slug === '') ?? null;
}

/**
 * De homepage mag alleen verdwijnen als er daarna geen andere pagina's meer
 * zijn -- anders zou de website pagina's houden zonder ingang, en zou
 * publiceren stilzwijgend onmogelijk worden. De bouwer blokkeert dit vooraf
 * met uitleg in plaats van achteraf met een foutmelding.
 */
export function pageDeletionBlockedReason(state: EditorState, pageId: string): string | null {
  const page = state.pages.find((item) => item.pageId === pageId);
  if (!page) return null;
  if (page.slug === '' && state.pages.length > 1) {
    return 'Dit is de homepage. Kies bij een andere pagina "Instellen als homepage" voordat je deze verwijdert.';
  }
  return null;
}

function snapshot(state: EditorState): HistorySnapshot {
  return {
    pages: state.pages,
    site: state.site,
    selectedPageId: state.selectedPageId,
    selectedSectionId: state.selectedSectionId,
    selectedBlockId: state.selectedBlockId,
  };
}

interface CommitOptions {
  coalesceKey?: string | null;
  selectedPageId?: string | null;
  selectedSectionId?: string | null;
  selectedBlockId?: string | null;
}

/** De enige weg waarlangs `pages` verandert. */
function commit(state: EditorState, pages: SitePage[], options: CommitOptions = {}): EditorState {
  const coalesceKey = options.coalesceKey ?? null;
  const coalesces = coalesceKey !== null && coalesceKey === state.coalesceKey;
  const past = coalesces ? state.past : [...state.past, snapshot(state)].slice(-MAX_HISTORY);

  return {
    ...state,
    pages,
    past,
    future: [],
    coalesceKey,
    changeSeq: state.changeSeq + 1,
    selectedPageId:
      options.selectedPageId !== undefined ? options.selectedPageId : state.selectedPageId,
    selectedSectionId:
      options.selectedSectionId !== undefined ? options.selectedSectionId : state.selectedSectionId,
    selectedBlockId:
      options.selectedBlockId !== undefined ? options.selectedBlockId : state.selectedBlockId,
    // Een conflict blijft staan tot de gebruiker het expliciet oplost; tot
    // die tijd wordt er niet opgeslagen, maar mag er wel doorgewerkt worden.
    saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'dirty',
    saveError: state.saveStatus === 'conflict' ? state.saveError : null,
  };
}

function mapPage(
  pages: SitePage[],
  pageId: string | null,
  update: (page: SitePage) => SitePage,
): SitePage[] {
  if (!pageId) return pages;
  return pages.map((page) => (page.pageId === pageId ? update(page) : page));
}

function flattenSections(page: SitePage): ContentBlock[] {
  return page.sections?.flatMap((section) => section.columns.flatMap((column) => column.blocks)) ?? page.blocks ?? [];
}

function canonicalSections(page: SitePage): SiteSection[] {
  return page.sections?.length
    ? page.sections
    : [{
        sectionId: createBlockId(),
        layout: 'content',
        columns: [{ columnId: createBlockId(), blocks: page.blocks ?? [] }],
        spacing: 'md',
      }];
}

function withSectionGeometry(section: SiteSection): SiteSection {
  const authoredHeight = section.style?.height;
  if (!authoredHeight) return section;

  // Older local Builder revisions materialized their automatic 560px-ish
  // height into the draft. Treat any value at or below the current derived
  // content requirement as automatic again; only a larger height is an
  // explicit owner choice and should remain as the section's minimum height.
  const styleWithoutHeight = section.style ? { ...section.style, height: undefined } : undefined;
  const automaticHeight = materializeSectionHeight(styleWithoutHeight, section).desktop;
  if (authoredHeight.desktop > automaticHeight || authoredHeight.tablet != null || authoredHeight.mobile != null) return section;
  const { height: _height, ...style } = section.style ?? {};
  return { ...section, style };
}

/**
 * `blocks` is deliberately only a generated compatibility projection. All
 * editor mutations below start from and write back to `sections`.
 */
function withCanonicalSections(page: SitePage, sections: SiteSection[]): SitePage {
  return { ...page, sections, blocks: sections.flatMap((section) => section.columns.flatMap((column) => column.blocks)) };
}

/**
 * Bring one loaded page onto the canonical model, exactly once.
 *
 * This is the ONLY place a legacy flat `blocks` array is ever read as
 * authoring input, and it runs only at load. Everything after it treats
 * `sections` as the single writable truth and regenerates `blocks` as a
 * projection (`withCanonicalSections`).
 *
 * The distinction matters more than it looks. The previous implementation
 * reconciled the two directions on every load: it rebuilt sections *from* the
 * flat list, matching by block id and appending anything unmatched to the
 * first column. That is a merge, and a merge needs a conflict rule. It had
 * one, implicitly -- the flat list wins -- which meant any section-only edit
 * that had not yet made it into `blocks` was silently relocated to column one
 * on the next load. Two writable truths cannot be kept honest by being
 * careful; they have to stop being two.
 *
 * A page that already has sections is therefore taken as-is. A page that has
 * only legacy blocks becomes one single-column section holding them in order:
 * lossless, and shaped so the user can immediately split it.
 */
function migrateLegacyPage(page: SitePage): SitePage {
  if (page.sections?.length) {
    return withCanonicalSections(page, page.sections.map(withSectionGeometry));
  }
  const legacyBlocks = page.blocks ?? [];
  return withCanonicalSections(page, [withSectionGeometry({
    sectionId: createBlockId(),
    layout: 'content',
    columnLayout: '1',
    columns: [{ columnId: createBlockId(), blocks: legacyBlocks }],
    spacing: 'md',
  })]);
}

function move<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'loadStarted':
      return { ...initialEditorState, status: 'loading' };

    case 'loaded': {
      const pages = action.draft.pages.map(migrateLegacyPage);
      const loadedSite = action.draft.site
        ? {
            ...action.draft.site,
            footer: {
              ...action.draft.site.footer,
              height: configuredFooterMinimumHeight(action.draft.site.footer.height),
              minHeight: configuredFooterMinimumHeight(action.draft.site.footer.minHeight),
              columns: action.draft.site.footer.columns.map((column) => ({
                ...column,
                elements: column.elements.map((element) => {
                  if (element.freeformLayout?.placementVersion !== 2) return element;
                  const { layout: _legacyLayout, ...v2Element } = element;
                  return v2Element;
                }),
              })),
            },
          }
        : null;
      return {
        ...initialEditorState,
        status: 'ready',
        draftId: action.draft.draftId,
        websiteId: action.draft.websiteId,
        revision: action.draft.revision,
        pages,
        site: loadedSite,
        selectedPageId: (homepageOf(pages) ?? pages[0])?.pageId ?? null,
        selectedSectionId: null,
        saveStatus: 'idle',
      };
    }

    case 'loadFailed':
      return { ...state, status: 'error', loadError: action.message };

    case 'selectPage':
      return { ...state, selectedPageId: action.pageId, selectedSectionId: null, selectedBlockId: null, coalesceKey: null };

    case 'selectSection':
      return { ...state, selectedSectionId: action.sectionId, selectedBlockId: null, coalesceKey: null };

    case 'selectBlock':
      return { ...state, selectedSectionId: null, selectedBlockId: action.blockId, coalesceKey: null };

    case 'updateSite': {
      const site = { ...(state.site ?? {} as SiteChrome), ...action.patch } as SiteChrome;
      return { ...commit(state, state.pages, { coalesceKey: 'site' }), site };
    }

    case 'updateTheme': {
      if (!state.site) return state;
      const site: SiteChrome = { ...state.site, theme: { ...state.site.theme, ...action.patch } };
      return { ...commit(state, state.pages, { coalesceKey: 'theme' }), site };
    }

    case 'updateHeader': {
      if (!state.site) return state;
      const site: SiteChrome = { ...state.site, header: { ...state.site.header, ...action.patch } };
      return { ...commit(state, state.pages, { coalesceKey: 'header' }), site };
    }

    case 'updateFooter': {
      if (!state.site) return state;
      const patch = { ...action.patch };
      if ('height' in patch) patch.height = configuredFooterMinimumHeight(patch.height);
      if ('minHeight' in patch) patch.minHeight = configuredFooterMinimumHeight(patch.minHeight);
      const site: SiteChrome = { ...state.site, footer: { ...state.site.footer, ...patch } };
      return { ...commit(state, state.pages, { coalesceKey: 'footer' }), site };
    }

    case 'updateSettings': {
      if (!state.site) return state;
      const site: SiteChrome = { ...state.site, settings: { ...state.site.settings, ...action.patch } };
      return { ...commit(state, state.pages, { coalesceKey: 'settings' }), site };
    }

    case 'updateSection': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page).map((section) => section.sectionId === action.sectionId ? { ...section, ...action.patch } : section);
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, sections)), { coalesceKey: `section:${action.sectionId}` });
    }

    case 'addPage': {
      const isFirstHomepage = homepageOf(state.pages) === null;
      const page: SitePage = {
        pageId: createPageId(),
        // De eerste pagina van een website wordt vanzelf de homepage: een
        // website zonder homepage kan niet gepubliceerd worden, en de
        // gebruiker hoeft dat begrip niet te kennen om te beginnen.
        slug: isFirstHomepage ? '' : uniqueSlug(action.slug || slugFromTitle(action.title), state.pages),
        title: action.title,
        description: action.description ?? null,
        blocks: [],
        sections: [{ sectionId: createBlockId(), layout: 'content', columns: [{ columnId: createBlockId(), blocks: [] }], spacing: 'md' }],
      };
      return commit(state, [...state.pages, page], {
        selectedPageId: page.pageId,
        selectedSectionId: page.sections?.[0]?.sectionId ?? null,
        selectedBlockId: null,
      });
    }

    case 'updatePage': {
      const target = state.pages.find((page) => page.pageId === action.pageId);
      if (!target) return state;
      const unchanged = (Object.keys(action.patch) as (keyof typeof action.patch)[]).every(
        (key) => target[key] === action.patch[key],
      );
      if (unchanged) return state;
      const pages = mapPage(state.pages, action.pageId, (page) => ({ ...page, ...action.patch }));
      return commit(state, pages, { coalesceKey: `page:${action.pageId}` });
    }

    case 'deletePage': {
      if (pageDeletionBlockedReason(state, action.pageId)) return state;
      const index = state.pages.findIndex((page) => page.pageId === action.pageId);
      const pages = state.pages.filter((page) => page.pageId !== action.pageId);
      const fallback = pages[Math.min(index, pages.length - 1)] ?? null;
      return commit(state, pages, {
        selectedPageId:
          state.selectedPageId === action.pageId ? fallback?.pageId ?? null : state.selectedPageId,
        selectedBlockId: null,
        selectedSectionId: null,
      });
    }

    case 'duplicatePage': {
      const index = state.pages.findIndex((page) => page.pageId === action.pageId);
      if (index === -1) return state;
      const source = state.pages[index]!;

      // Every authored identity is regenerated. Two pages sharing a section,
      // column or element id is not a cosmetic problem: the server refuses
      // the draft outright (ids are unique per website), so a copy that
      // reused them would make the whole site unsaveable rather than merely
      // odd.
      const sections = canonicalSections(source).map((section) => ({
        ...section,
        sectionId: createBlockId(),
        columns: section.columns.map((column) => ({
          ...column,
          columnId: createBlockId(),
          blocks: column.blocks.map((block) => ({ ...block, blockId: createBlockId() })),
        })),
      }));

      // A copy is never the homepage: the homepage is the one page whose slug
      // is "", and a second one would make the draft invalid. It gets a real
      // address derived from its title instead.
      const title = `${source.title || 'Naamloze pagina'} (kopie)`;
      const copy: SitePage = withCanonicalSections({
        ...source,
        pageId: createPageId(),
        title,
        slug: uniqueSlug(slugFromTitle(title) || 'pagina', state.pages),
        seo: source.seo ? { ...source.seo } : undefined,
        navigation: source.navigation ? { ...source.navigation } : null,
      }, sections);

      const pages = state.pages.slice();
      pages.splice(index + 1, 0, copy);
      return commit(state, pages, {
        selectedPageId: copy.pageId,
        selectedSectionId: null,
        selectedBlockId: null,
      });
    }

    case 'makeHomepage': {
      const target = state.pages.find((page) => page.pageId === action.pageId);
      if (!target || target.slug === '') return state;
      const previous = homepageOf(state.pages);
      let pages = state.pages;
      if (previous) {
        // De oude homepage krijgt een echt adres op basis van haar titel --
        // nooit stilzwijgend weggegooid, nooit een tweede pagina op "/".
        const replacementSlug = uniqueSlug(
          slugFromTitle(previous.title),
          pages.filter((page) => page.pageId !== action.pageId),
          previous.pageId,
        );
        pages = mapPage(pages, previous.pageId, (page) => ({ ...page, slug: replacementSlug }));
      }
      pages = mapPage(pages, action.pageId, (page) => ({ ...page, slug: '' }));
      return commit(state, pages);
    }

    case 'addBlock': {
      const page = currentPage(state);
      if (!page) return state;
      const block = createBlock(action.blockType);
      const sections = canonicalSections(page);
      if (action.columnId) {
        const nextSections = sections.map((section) => ({
          ...section,
          columns: section.columns.map((column) => {
            if (section.sectionId !== action.sectionId || column.columnId !== action.columnId) return column;
            return { ...column, blocks: [...column.blocks, block] };
          }),
        }));
        return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), { selectedSectionId: null, selectedBlockId: block.blockId });
      }
      const target = sections[0]!.columns[0]!;
      const index = action.index ?? target.blocks.length;
      const nextSections = sections.map((section, sectionIndex) => sectionIndex === 0 ? { ...section, columns: section.columns.map((column, columnIndex) => columnIndex === 0 ? { ...column, blocks: [...column.blocks.slice(0, index), block, ...column.blocks.slice(index)] } : column) } : section);
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        selectedSectionId: null,
        selectedBlockId: block.blockId,
      });
    }

    case 'addSection': {
      const page = currentPage(state);
      if (!page || action.blocks.length === 0) return state;
      const index = action.index ?? flattenSections(page).length;
      // A preset may state its layout directly; `columnCount` remains
      // supported for callers that only know how many columns they want.
      const columnLayout: ColumnLayout = action.columnLayout
        ?? (String(Math.max(1, Math.min(action.columnCount ?? 1, 4))) as ColumnLayout);
      const columnCount = COLUMN_COUNTS[columnLayout] ?? 1;
      const splitAt = Math.ceil(action.blocks.length / columnCount);
      const columns = Array.from({ length: columnCount }, (_, columnIndex) => ({
        columnId: createBlockId(),
        blocks: action.blocks.slice(columnIndex * splitAt, (columnIndex + 1) * splitAt),
      }));
      const newSection: SiteSection = {
        sectionId: createBlockId(),
        layout: 'content',
        columnLayout,
        columns,
        spacing: 'md',
        ...(action.style ? { style: { ...action.style } } : {}),
        ...(action.presetId ? { presetId: action.presetId } : {}),
      };
      return commit(state, mapPage(state.pages, page.pageId, (item) => {
        const sections = canonicalSections(item).slice();
        let remaining = 0;
        let insertAt = sections.length;
        for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
          remaining += sections[sectionIndex]!.columns.reduce((sum, column) => sum + column.blocks.length, 0);
          if (index < remaining) { insertAt = sectionIndex; break; }
        }
        sections.splice(insertAt, 0, newSection);
        return withCanonicalSections(item, sections);
      }), {
        selectedSectionId: newSection.sectionId,
        selectedBlockId: null,
      });
    }

    case 'moveSection':
    case 'reorderSections': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const from = action.type === 'moveSection'
        ? sections.findIndex((section) => section.sectionId === action.sectionId)
        : action.from;
      const to = action.type === 'moveSection'
        ? from + (action.direction === 'up' ? -1 : 1)
        : action.to;
      if (from === -1) return state;
      const nextSections = move(sections, from, to);
      // `move` returns the same array for a no-op (already first and moving
      // up, already last and moving down). Returning `state` unchanged keeps
      // that out of the undo history and off the autosave queue, so holding
      // the up arrow at the top of a page does not queue twenty saves.
      if (nextSections === sections) return state;
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)));
    }

    case 'changeSectionLayout': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const target = sections.find((section) => section.sectionId === action.sectionId);
      if (!target || target.columnLayout === action.layout) return state;

      const nextCount = COLUMN_COUNTS[action.layout];
      const existing = target.columns;
      const columns = Array.from({ length: nextCount }, (_, index) => existing[index] ?? {
        columnId: createBlockId(),
        blocks: [] as ContentBlock[],
      });

      // Reducing the column count must never destroy content (SITES-MISSION
      // §13). Everything from the columns that no longer exist is appended,
      // in order, to the last surviving column -- so "4 columns -> 2" moves
      // content the user can see and drag back, instead of quietly deleting
      // two columns' worth of work. Appending rather than interleaving keeps
      // the result predictable: the reading order of the whole section is
      // unchanged.
      const orphaned = existing.slice(nextCount).flatMap((column) => column.blocks);
      if (orphaned.length) {
        const last = columns[nextCount - 1]!;
        columns[nextCount - 1] = { ...last, blocks: [...last.blocks, ...orphaned] };
      }

      const nextSections = sections.map((section) => section.sectionId === action.sectionId
        ? { ...section, columnLayout: action.layout, columns }
        : section);
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        selectedSectionId: action.sectionId,
      });
    }

    case 'moveBlockToColumn': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const moving = sections
        .flatMap((section) => section.columns.flatMap((column) => column.blocks))
        .find((block) => block.blockId === action.blockId);
      if (!moving) return state;

      // Removed everywhere first, then inserted once. Doing it in one pass
      // risks the source and target being the same column, where a naive
      // splice-then-insert shifts the target index underneath itself.
      const withoutBlock = sections.map((section) => ({
        ...section,
        columns: section.columns.map((column) => ({
          ...column,
          blocks: column.blocks.filter((block) => block.blockId !== action.blockId),
        })),
      }));
      const nextSections = withoutBlock.map((section) => section.sectionId !== action.sectionId ? section : {
        ...section,
        columns: section.columns.map((column) => {
          if (column.columnId !== action.columnId) return column;
          const index = Math.max(0, Math.min(action.index ?? column.blocks.length, column.blocks.length));
          return { ...column, blocks: [...column.blocks.slice(0, index), moving, ...column.blocks.slice(index)] };
        }),
      });

      // If the target column did not exist, nothing was re-inserted and the
      // block would vanish. Refuse the whole operation rather than commit a
      // page that lost an element.
      const survives = nextSections.some((section) => section.columns.some((column) =>
        column.blocks.some((block) => block.blockId === action.blockId)));
      if (!survives) return state;

      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        selectedSectionId: null,
        selectedBlockId: action.blockId,
      });
    }

    case 'reorderBlockInColumn': {
      const page = currentPage(state);
      if (!page) return state;
      let changed = false;
      const nextSections = canonicalSections(page).map((section) => section.sectionId !== action.sectionId ? section : {
        ...section,
        columns: section.columns.map((column) => {
          if (column.columnId !== action.columnId) return column;
          const blocks = move(column.blocks, action.from, action.to);
          if (blocks !== column.blocks) changed = true;
          return { ...column, blocks };
        }),
      });
      if (!changed) return state;
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)));
    }

    case 'insertSection': {
      // A preset arrives fully formed: its own columns, layout and style. The
      // reducer only decides WHERE it lands and stamps a fresh section id --
      // the ids inside were already regenerated by `instantiatePreset`.
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page).slice();
      const newSection: SiteSection = {
        sectionId: createBlockId(),
        layout: 'content',
        spacing: 'md',
        ...action.section,
      };
      const sectionWithGeometry = withSectionGeometry(newSection);
      const insertAt = action.index === undefined
        ? sections.length
        : Math.max(0, Math.min(action.index, sections.length));
      sections.splice(insertAt, 0, sectionWithGeometry);
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, sections)), {
        selectedSectionId: sectionWithGeometry.sectionId,
        selectedBlockId: null,
      });
    }

    case 'deleteSection': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const index = sections.findIndex((section) => section.sectionId === action.sectionId);
      if (index === -1) return state;
      const nextSections = sections.filter((section) => section.sectionId !== action.sectionId);
      const nextSection = nextSections[Math.min(index, nextSections.length - 1)] ?? null;
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        selectedSectionId: nextSection?.sectionId ?? null,
        selectedBlockId: null,
      });
    }

    case 'duplicateSection': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const index = sections.findIndex((section) => section.sectionId === action.sectionId);
      if (index === -1) return state;
      const source = sections[index]!;
      const copy: SiteSection = {
        ...source,
        sectionId: createBlockId(),
        columns: source.columns.map((column) => ({
          ...column,
          columnId: createBlockId(),
          blocks: column.blocks.map((block) => ({ ...block, blockId: createBlockId() })),
        })),
      };
      const nextSections = sections.slice();
      nextSections.splice(index + 1, 0, copy);
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        selectedSectionId: copy.sectionId,
        selectedBlockId: null,
      });
    }

    case 'setBlockText': {
      const page = currentPage(state);
      if (!page) return state;
      // Een bewerking die niets verandert is geen bewerking. Zonder deze
      // controle markeert alleen al het verlaten van een tekstblok (de
      // blur-synchronisatie in InlineText) het concept als "niet opgeslagen"
      // en volgt er een zinloze opslagronde.
      const existing = canonicalSections(page).flatMap((section) => section.columns.flatMap((column) => column.blocks)).find((block) => block.blockId === action.blockId);
      if (!existing || existing.text === action.text) return state;
      const sections = canonicalSections(page).map((section) => ({ ...section, columns: section.columns.map((column) => ({ ...column, blocks: column.blocks.map((block) => block.blockId === action.blockId ? { ...block, text: action.text } : block) })) }));
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, sections)), {
        coalesceKey: `text:${action.blockId}`,
      });
    }

    case 'updateBlock': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const exists = sections.some((section) =>
        section.columns.some((column) => column.blocks.some((block) => block.blockId === action.blockId)));
      if (!exists) return state;
      const nextSections = sections.map((section) => ({
        ...section,
        columns: section.columns.map((column) => ({
          ...column,
          blocks: column.blocks.map((block) =>
            block.blockId === action.blockId ? { ...block, ...action.patch } : block),
        })),
      }));
      // Coalesced per block, so dragging a slider or typing into a button
      // label becomes one undo step rather than one per keystroke -- the same
      // rule `setBlockText` already follows.
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        coalesceKey: `block:${action.blockId}`,
      });
    }

    case 'updateBlockPlacement': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page).map((section) => ({
        ...section,
        columns: section.columns.map((column, columnIndex) => ({
          ...column,
          blocks: column.blocks.map((block) => {
            const baseLayout = block.layout ?? layoutFromColumn(section.columnLayout, columnIndex);
            return block.blockId === action.blockId
              ? { ...block, layout: setPlacement(baseLayout, action.viewport, action.patch) }
              : { ...block, layout: baseLayout };
          }),
        })),
      }));
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, sections)), {
        coalesceKey: `placement:${action.blockId}:${action.viewport}`,
      });
    }

    case 'deleteBlock': {
      const page = currentPage(state);
      if (!page) return state;
      const flat = canonicalSections(page).flatMap((section) => section.columns.flatMap((column) => column.blocks));
      const index = flat.findIndex((block) => block.blockId === action.blockId);
      if (index === -1) return state;
      const neighbour = flat.filter((block) => block.blockId !== action.blockId)[Math.min(index, flat.length - 2)] ?? null;
      const sections = canonicalSections(page).map((section) => ({ ...section, columns: section.columns.map((column) => ({ ...column, blocks: column.blocks.filter((block) => block.blockId !== action.blockId) })) }));
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, sections)), {
        selectedBlockId: state.selectedBlockId === action.blockId ? neighbour?.blockId ?? null : state.selectedBlockId,
      });
    }

    case 'duplicateBlock': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const flat = sections.flatMap((section) => section.columns.flatMap((column) => column.blocks));
      const index = flat.findIndex((block) => block.blockId === action.blockId);
      if (index === -1) return state;
      const source = flat[index];
      const copy: ContentBlock = { ...source, blockId: createBlockId() };
      const nextSections = sections.map((section) => ({ ...section, columns: section.columns.map((column) => ({ ...column, blocks: column.blocks.flatMap((block) => block.blockId === action.blockId ? [block, copy] : [block]) })) }));
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)), {
        selectedSectionId: null,
        selectedBlockId: copy.blockId,
      });
    }

    case 'moveBlock': {
      const page = currentPage(state);
      if (!page) return state;
      const sections = canonicalSections(page);
      const flat = sections.flatMap((section) => section.columns.flatMap((column) => column.blocks));
      const blocks = move(flat, action.from, action.to);
      if (blocks === flat) return state;
      let cursor = 0;
      const nextSections = sections.map((section) => ({
        ...section,
        columns: section.columns.map((column) => ({
          ...column,
          blocks: column.blocks.map(() => blocks[cursor++]!),
        })),
      }));
      return commit(state, mapPage(state.pages, page.pageId, (item) => withCanonicalSections(item, nextSections)));
    }

    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...state,
        pages: previous.pages,
        site: previous.site,
        selectedPageId: previous.selectedPageId,
        selectedSectionId: previous.selectedSectionId,
        selectedBlockId: previous.selectedBlockId,
        past: state.past.slice(0, -1),
        future: [snapshot(state), ...state.future].slice(0, MAX_HISTORY),
        changeSeq: state.changeSeq + 1,
        coalesceKey: null,
        saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'dirty',
      };
    }

    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      return {
        ...state,
        pages: next.pages,
        site: next.site,
        selectedPageId: next.selectedPageId,
        selectedSectionId: next.selectedSectionId,
        selectedBlockId: next.selectedBlockId,
        past: [...state.past, snapshot(state)].slice(-MAX_HISTORY),
        future: state.future.slice(1),
        changeSeq: state.changeSeq + 1,
        coalesceKey: null,
        saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'dirty',
      };
    }

    case 'saveStarted':
      return { ...state, saveStatus: 'saving', inFlightSeq: action.seq, saveError: null };

    case 'saveSucceeded': {
      // Alleen wat daadwerkelijk verstuurd is geldt als opgeslagen. Typte de
      // gebruiker door tijdens de opslag, dan blijft het concept "niet
      // opgeslagen" en gaat er direct een volgende opslag achteraan.
      const stillDirty = state.changeSeq !== action.seq;
      return {
        ...state,
        revision: action.revision,
        savedSeq: action.seq,
        inFlightSeq: null,
        saveStatus: stillDirty ? 'dirty' : 'saved',
        saveError: null,
        lastSavedAt: Date.now(),
      };
    }

    case 'saveFailed':
      // De lokale bewerkingen blijven onaangeroerd staan -- een mislukte
      // opslag mag nooit werk weggooien.
      return { ...state, saveStatus: 'error', saveError: action.message, inFlightSeq: null };

    case 'saveConflict':
      return {
        ...state,
        saveStatus: 'conflict',
        inFlightSeq: null,
        conflict: { serverDraft: action.serverDraft },
        saveError: null,
      };

    case 'resolveConflictTakeServer': {
      const draft = state.conflict?.serverDraft;
      if (!draft) return state;
      return {
        ...initialEditorState,
        status: 'ready',
        draftId: draft.draftId,
        websiteId: draft.websiteId,
        revision: draft.revision,
        pages: draft.pages,
        selectedPageId: (homepageOf(draft.pages) ?? draft.pages[0])?.pageId ?? null,
        selectedSectionId: null,
        saveStatus: 'idle',
        changeSeq: 0,
        savedSeq: 0,
      };
    }

    case 'resolveConflictKeepMine': {
      const draft = state.conflict?.serverDraft;
      if (!draft) return state;
      // Bewuste keuze van de gebruiker: verder op de nieuwe serverrevisie,
      // met de eigen versie van de inhoud. Dat is een geïnformeerde
      // overschrijving -- geen stilzwijgende last-write-wins.
      return { ...state, revision: draft.revision, conflict: null, saveStatus: 'dirty' };
    }

    case 'dismissError':
      return { ...state, saveStatus: isDirty(state) ? 'dirty' : 'idle', saveError: null };

    default:
      return state;
  }
}
